/**
 * God View: /admin/god-view. Loop-ADMIN-only. Every active account, with a
 * "View as" action that opens a full act-as session for that account. See
 * lib/auth/session.ts's startImpersonation() and
 * components/shell/ImpersonationBanner.tsx for the rest of the mechanism.
 *
 * Also where an account is banned and unbanned (the Banned tab). A ban is
 * cloud-api's POST /admin/accounts/:id/suspend: the person is signed out
 * everywhere and cannot sign in again, while their apps keep running. The
 * dialog says so, because "banned" alone reads as "their site is gone".
 *
 * Same posture as screens/admin/Waitlist.tsx (the screen this name used to
 * belong to): client-side `account.isAdmin` redirect is cosmetic, the real
 * gate is server-side (cloud-api's middleware/requireAdmin.ts); not wired
 * through lib/api.ts's mock/real seam, since the nav entry that reaches it
 * is hidden entirely in mock mode.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, Navigate } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { Dialog } from '../../components/primitives/Dialog';
import { Field } from '../../components/primitives/Field';
import { showToast } from '../../components/primitives/Toast';
import { Table, type TableColumn } from '../../components/primitives/Table';
import { Tabs } from '../../components/primitives/Tabs';
import { EmptyState } from '../../components/patterns/EmptyState';
import { Notice } from '../../components/patterns/Notice';
import { Skeleton } from '../../components/patterns/Skeleton';

import { useAccount } from '../../lib/auth/use-app-data';
import { getToken, startImpersonation } from '../../lib/auth/session';
import { useAsync } from '../../lib/hooks/use-async';
import { apiFetch, ApiError } from '../../lib/http';
import { t } from '../../lib/i18n/t';
import { captureEvent } from '../../lib/analytics/posthog';
import { useFormTracker } from '../../lib/analytics/use-form-tracker';

import '../../styles/screens/admin.css';

interface AccountWire {
  id: string;
  email: string;
  display_name: string | null;
  mode: 'simple' | 'advanced';
  is_admin: boolean;
  created_at: string;
  status: 'active' | 'suspended';
  suspended_at: string | null;
  suspended_reason: string | null;
}

interface ListResponse {
  accounts: AccountWire[];
  truncated: boolean;
}

interface ImpersonateResponse {
  token: string;
  expires_at: string;
  account: AccountWire;
}

interface ProjectOwnerWire {
  id: string;
  email: string;
  display_name: string | null;
}

interface ProjectAdminWire {
  id: string;
  name: string;
  subdomain: string;
  environment: 'production' | 'staging';
  status: string;
  created_at: string;
  owner: ProjectOwnerWire | null;
}

interface ProjectListResponse {
  projects: ProjectAdminWire[];
  truncated: boolean;
}

async function loadAccounts(status: AccountWire['status']): Promise<ListResponse> {
  return apiFetch<ListResponse>(`/api/v1/admin/accounts?status=${status}`);
}

type GodViewTab = 'accounts' | 'banned' | 'projects';

async function loadProjects(q: string): Promise<ProjectListResponse> {
  if (!q.trim()) return { projects: [], truncated: false };
  return apiFetch<ProjectListResponse>(`/api/v1/admin/projects?q=${encodeURIComponent(q)}`);
}

export default function GodView() {
  const account = useAccount();
  const navigate = useNavigate();
  const [tab, setTab] = useState<GodViewTab>('accounts');
  const { data, isLoading, error, refetch } = useAsync(() => loadAccounts('active'), []);
  const {
    data: bannedData,
    isLoading: bannedLoading,
    error: bannedError,
    refetch: refetchBanned,
  } = useAsync(() => loadAccounts('suspended'), []);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [banTarget, setBanTarget] = useState<AccountWire | null>(null);
  const [banReason, setBanReason] = useState('');
  const [banning, setBanning] = useState(false);
  const banForm = useFormTracker({ formId: 'admin_ban_account', formName: 'Ban account', totalFields: 1 });

  const [projectQuery, setProjectQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const {
    data: projectData,
    isLoading: projectsLoading,
    error: projectsError,
  } = useAsync(() => loadProjects(submittedQuery), [submittedQuery]);

  useEffect(() => {
    if (account.isAdmin && data) {
      captureEvent('admin_god_view_viewed', {
        total_accounts: data.accounts.length,
      });
    }
  }, [account.isAdmin, data]);

  useEffect(() => {
    if (account.isAdmin && projectData && submittedQuery) {
      captureEvent('admin_god_view_project_search', {
        query_length: submittedQuery.length,
        result_count: projectData.projects.length,
      });
    }
  }, [account.isAdmin, projectData, submittedQuery]);

  if (!account.isAdmin) {
    return <Navigate to="/projects" replace />;
  }

  async function viewAs(target: { id: string; email: string }) {
    setBusyId(target.id);
    captureEvent('admin_impersonation_started', { target_account_id: target.id });
    try {
      const adminToken = getToken();
      if (!adminToken) throw new Error('Missing admin session token.');
      const res = await apiFetch<ImpersonateResponse>(
        `/api/v1/admin/accounts/${target.id}/impersonate`,
        { method: 'POST' },
      );
      startImpersonation(res.token, adminToken, account.email, res.expires_at);
      navigate('/projects');
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : t('Something went wrong. Try again in a moment.'));
      setBusyId(null);
    }
  }

  function openBan(target: AccountWire) {
    captureEvent('admin_account_ban_clicked', { target_account_id: target.id });
    setBanReason('');
    setBanTarget(target);
  }

  function closeBan() {
    if (banning) return;
    banForm.trackCancel('dialog_closed');
    setBanTarget(null);
  }

  async function confirmBan(e: FormEvent) {
    e.preventDefault();
    if (!banTarget) return;
    setBanning(true);
    banForm.trackSubmit({ has_reason: banReason.trim().length > 0 });
    try {
      await apiFetch(`/api/v1/admin/accounts/${banTarget.id}/suspend`, {
        method: 'POST',
        body: { reason: banReason },
      });
      banForm.trackSubmitSuccess({ target_account_id: banTarget.id });
      showToast(t('{email} is banned.', { email: banTarget.email }));
      setBanTarget(null);
      refetch();
      refetchBanned();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('Something went wrong. Try again in a moment.');
      banForm.trackSubmitError(err instanceof ApiError ? err.code : 'unknown', message);
      showToast(message);
    } finally {
      setBanning(false);
    }
  }

  async function unban(target: AccountWire) {
    setBusyId(target.id);
    captureEvent('admin_account_unbanned', { target_account_id: target.id });
    try {
      await apiFetch(`/api/v1/admin/accounts/${target.id}/unsuspend`, { method: 'POST' });
      showToast(t('{email} can sign in again.', { email: target.email }));
      refetch();
      refetchBanned();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : t('Something went wrong. Try again in a moment.'));
    } finally {
      setBusyId(null);
    }
  }

  function submitProjectSearch(e: FormEvent) {
    e.preventDefault();
    setSubmittedQuery(projectQuery);
  }

  const columns: TableColumn<AccountWire>[] = [
    { key: 'email', header: t('Email'), render: (row) => row.email },
    { key: 'name', header: t('Name'), render: (row) => row.display_name ?? '' },
    { key: 'plan', header: t('Mode'), render: (row) => row.mode },
    {
      key: 'created',
      header: t('Joined'),
      render: (row) => new Date(row.created_at).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: t('Actions'),
      align: 'end',
      render: (row) => (
        <div className="admin__actions">
          <Button
            size="sm"
            variant="outline"
            loading={busyId === row.id}
            onClick={() => viewAs(row)}
          >
            {t('View as')}
          </Button>
          {!row.is_admin && row.id !== account.id && (
            <Button size="sm" variant="danger-outline" onClick={() => openBan(row)}>
              {t('Ban')}
            </Button>
          )}
        </div>
      ),
    },
  ];

  const bannedColumns: TableColumn<AccountWire>[] = [
    { key: 'email', header: t('Email'), render: (row) => row.email },
    { key: 'reason', header: t('Reason'), render: (row) => row.suspended_reason ?? '' },
    {
      key: 'banned',
      header: t('Banned'),
      render: (row) => (row.suspended_at ? new Date(row.suspended_at).toLocaleDateString() : ''),
    },
    {
      key: 'actions',
      header: t('Actions'),
      align: 'end',
      render: (row) => (
        <div className="admin__actions">
          <Button size="sm" variant="outline" loading={busyId === row.id} onClick={() => unban(row)}>
            {t('Unban')}
          </Button>
        </div>
      ),
    },
  ];

  const projectColumns: TableColumn<ProjectAdminWire>[] = [
    {
      key: 'name',
      header: t('Project'),
      render: (row) => (
        <div>
          <div>{row.name}</div>
          <div className="mono admin__subdomain">{row.subdomain}</div>
        </div>
      ),
    },
    {
      key: 'owner',
      header: t('Owner'),
      render: (row) => row.owner?.display_name ?? row.owner?.email ?? t('No owner'),
    },
    {
      key: 'created',
      header: t('Created'),
      render: (row) => new Date(row.created_at).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: t('Actions'),
      align: 'end',
      render: (row) =>
        row.owner ? (
          <div className="admin__actions">
            <Button
              size="sm"
              variant="outline"
              loading={busyId === row.owner.id}
              onClick={() => row.owner && viewAs(row.owner)}
            >
              {t('View as')}
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">{t('God View')}</h1>
        <p className="page__lede">{t('Every active account. View as one to see their dashboard exactly as they would.')}</p>
      </header>

      <Tabs
        label={t('God View sections')}
        value={tab}
        onChange={(id) => setTab(id as GodViewTab)}
        tabs={[
          { id: 'accounts', label: t('Accounts') },
          { id: 'banned', label: t('Banned') },
          { id: 'projects', label: t('Projects') },
        ]}
      >
        {tab === 'accounts' && (
          <>
            {isLoading && <Skeleton shape="row" count={4} label={t('Loading accounts')} />}

            {error && <Notice tone="attention">{t('Could not load accounts. Try again in a moment.')}</Notice>}

            {data && data.accounts.length === 0 && (
              <EmptyState mark="box" sentence={t('No active accounts yet.')} />
            )}

            {data && data.accounts.length > 0 && (
              <Table
                caption={t('Accounts')}
                columns={columns}
                rows={data.accounts}
                rowKey={(row) => row.id}
              />
            )}

            {data?.truncated && <p className="page__lede">{t('Showing the most recent accounts only.')}</p>}
          </>
        )}

        {tab === 'banned' && (
          <>
            <p className="page__lede">
              {t('Banned accounts cannot sign in to SV Cloud, the CLI, or MCP. Their apps keep running.')}
            </p>

            {bannedLoading && <Skeleton shape="row" count={2} label={t('Loading banned accounts')} />}

            {bannedError && (
              <Notice tone="attention">{t('Could not load banned accounts. Try again in a moment.')}</Notice>
            )}

            {bannedData && bannedData.accounts.length === 0 && (
              <EmptyState mark="box" sentence={t('Nobody is banned.')} />
            )}

            {bannedData && bannedData.accounts.length > 0 && (
              <Table
                caption={t('Banned accounts')}
                columns={bannedColumns}
                rows={bannedData.accounts}
                rowKey={(row) => row.id}
              />
            )}
          </>
        )}

        {tab === 'projects' && (
          <>
            <form className="admin__search" onSubmit={submitProjectSearch}>
              <input
                type="text"
                className="admin__search-input"
                placeholder={t('Search projects by name...')}
                value={projectQuery}
                onChange={(e) => setProjectQuery(e.target.value)}
                aria-label={t('Search projects by name')}
              />
              <Button type="submit" size="sm" variant="outline">
                {t('Search')}
              </Button>
            </form>

            {projectsLoading && <Skeleton shape="row" count={4} label={t('Searching projects')} />}

            {projectsError && (
              <Notice tone="attention">{t('Could not search projects. Try again in a moment.')}</Notice>
            )}

            {!submittedQuery && !projectsLoading && !projectsError && (
              <EmptyState mark="box" sentence={t('Search for a project by name to find its owner.')} />
            )}

            {submittedQuery &&
              !projectsLoading &&
              !projectsError &&
              projectData &&
              projectData.projects.length === 0 && (
                <EmptyState mark="box" sentence={t('No projects match that search.')} />
              )}

            {!projectsLoading && !projectsError && projectData && projectData.projects.length > 0 && (
              <Table
                caption={t('Projects')}
                columns={projectColumns}
                rows={projectData.projects}
                rowKey={(row) => row.id}
              />
            )}

            {!projectsLoading && !projectsError && projectData?.truncated && (
              <p className="page__lede">{t('Showing the most recent matching projects only.')}</p>
            )}
          </>
        )}
      </Tabs>

      <Dialog
        open={banTarget !== null}
        onClose={closeBan}
        title={t('Ban {email}?', { email: banTarget?.email ?? '' })}
        description={t(
          'They are signed out everywhere and cannot sign in again until you unban them. Their apps keep running.',
        )}
      >
        <form onSubmit={confirmBan}>
          <Field
            label={t('Reason')}
            help={t('Only admins see this.')}
            value={banReason}
            maxLength={500}
            data-autofocus
            onFocus={() => banForm.onFieldFocus('reason')}
            onBlur={() => banForm.onFieldBlur('reason')}
            onChange={(e) => {
              setBanReason(e.target.value);
              banForm.onFieldChange('reason', e.target.value);
            }}
          />
          <div className="dialog__foot">
            <Button type="button" variant="ghost" onClick={closeBan} disabled={banning}>
              {t('Cancel')}
            </Button>
            <Button type="submit" variant="danger-outline" loading={banning}>
              {t('Ban')}
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
